import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaGuitar,
  FaHome,
  FaCheckCircle,
} from "react-icons/fa";

export default function PartyMembers({ uid }) {
  const [participant, setParticipant] = useState(null);
  const [originalParticipant, setOriginalParticipant] = useState(null);
  const [payments, setPayments] = useState({});
  const [originalPayments, setOriginalPayments] = useState({});
  const [loading, setLoading] = useState(true);
  const [updateStatus, setUpdateStatus] = useState("");

  const isDirty = () => {
    return (
      JSON.stringify(participant) !== JSON.stringify(originalParticipant) ||
      JSON.stringify(payments) !== JSON.stringify(originalPayments)
    );
  };

  useEffect(() => {
    if (!uid) return;
    const fetchParticipant = async () => {
      try {
        const reservationRef = doc(db, "reservations", uid);
        const reservationSnap = await getDoc(reservationRef);
        if (reservationSnap.exists()) {
          const data = reservationSnap.data();
          setParticipant(data.participant);
          setOriginalParticipant(data.participant);
          setPayments(data.payments || {});
          setOriginalPayments(data.payments || {});
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching participant:", error);
      }
      setLoading(false);
    };
    fetchParticipant();
  }, [uid]);

  const handleChange = (field, value) => {
    setParticipant((prev) => ({ ...prev, [field]: value }));
  };

  const handlePaymentChange = (month, value) => {
    setPayments((prev) => ({ ...prev, [month]: value }));
  };

  const handleUpdate = async () => {
    try {
      const reservationRef = doc(db, "reservations", uid);
      await updateDoc(reservationRef, {
        participant,
        payments,
      });
      setOriginalParticipant(participant);
      setOriginalPayments(payments);
      setUpdateStatus("¡Información actualizada correctamente!");
    } catch (error) {
      console.error("Error updating participant:", error);
      setUpdateStatus("Error al actualizar.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-800">
        Cargando datos...
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-800">
        No se encontró información del participante.
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 flex flex-col items-center px-4 py-16">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl border-2 border-red-500 p-8"
      >
        <h1 className="text-3xl font-extrabold text-red-600 mb-6 text-center">
          Información del Participante
        </h1>

        {/* Participant Fields */}
        <div className="space-y-4">
          <Field
            icon={<FaUser className="text-red-500" />}
            label="Nombre"
            value={participant.firstName || ""}
            onChange={(e) => handleChange("firstName", e.target.value)}
          />
          <Field
            icon={<FaUser className="text-red-500" />}
            label="Apellido"
            value={participant.lastName || ""}
            onChange={(e) => handleChange("lastName", e.target.value)}
          />
          <Field
            icon={<FaEnvelope className="text-red-500" />}
            label="Email"
            value={participant.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
          />
          <Field
            icon={<FaPhone className="text-red-500" />}
            label="Teléfono"
            value={participant.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value)}
          />
          <Field
            icon={<FaCalendarAlt className="text-red-500" />}
            label="Edad"
            type="number"
            value={participant.age || ""}
            onChange={(e) => handleChange("age", e.target.value)}
          />
          <Field
            icon={<FaGuitar className="text-red-500" />}
            label="Instrumento"
            value={participant.instrument || ""}
            onChange={(e) => handleChange("instrument", e.target.value)}
          />
          <Field
            icon={<FaHome className="text-red-500" />}
            label="Dirección"
            value={participant.address || ""}
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </div>

        {/* Payments */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-red-600 mb-4 flex items-center">
            <FaCheckCircle className="mr-2 text-green-600" /> Pagos Mensuales
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(payments).map((month) => (
              <label
                key={month}
                className="flex items-center bg-red-50 border border-red-200 rounded-lg p-2 cursor-pointer hover:bg-red-100 transition"
              >
                <input
                  type="checkbox"
                  checked={payments[month]}
                  onChange={(e) =>
                    handlePaymentChange(month, e.target.checked)
                  }
                  className="mr-2"
                />
                {month}
              </label>
            ))}
          </div>
        </div>

        {/* Update Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleUpdate}
            disabled={!isDirty()}
            className={`px-6 py-3 rounded-lg font-semibold shadow-lg transition ${
              isDirty()
                ? "bg-red-600 hover:bg-red-500 text-white"
                : "bg-gray-400 text-gray-200 cursor-not-allowed"
            }`}
          >
            Actualizar Información
          </button>
        </div>
        {updateStatus && (
          <p className="mt-4 text-center text-green-600">{updateStatus}</p>
        )}
      </motion.div>
    </div>
  );
}

const Field = ({ icon, label, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
      <span className="px-3 bg-red-50">{icon}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="flex-1 px-3 py-2 bg-white text-gray-800 outline-none"
      />
    </div>
  </div>
);
